import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const LandingPage = () => {
  const { isAuthenticated } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const whyMyMDO = [
    {
      title: 'Multidisciplinary Optimization',
      description: 'Combine aerodynamics, structures, and other disciplines for better designs.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
    },
    {
      title: 'Save Time & Cost',
      description: 'Accelerate design cycles and reduce development costs.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Cloud-Based & Secure',
      description: 'Access your projects anytime, with data confidentiality guaranteed.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
  ];

  const features = [
    {
      title: 'Fast CFD Analysis',
      description: 'Quick insights for early-stage designs.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'primary',
    },
    {
      title: 'Optimization Tools',
      description: 'Improve performance using advanced algorithms.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: 'accent',
    },
    {
      title: 'Cloud Compute',
      description: 'Run simulations without investing in hardware.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      color: 'success',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Set Up Your Design',
      description: 'Define your parameters and goals.',
    },
    {
      number: '02',
      title: 'Run Simulation in the Cloud',
      description: 'Track progress in real time.',
    },
    {
      number: '03',
      title: 'Analyze Results',
      description: 'View outputs, export data, iterate quickly.',
    },
  ];

  const applications = [
    {
      title: 'Aerospace',
      description: 'Aircraft, drones, UAVs design optimization',
      primary: true,
    },
    {
      title: 'Automotive',
      description: 'Vehicle aerodynamics and structures',
      primary: false,
    },
    {
      title: 'Naval',
      description: 'Marine vessel optimization',
      primary: false,
    },
    {
      title: 'Energy',
      description: 'Wind turbines and energy systems',
      primary: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950">
      {/* Navigation - Glassmorphism style */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-900/60 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-4">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-green-400 via-yellow-400 to-blue-600 bg-clip-text text-transparent">
              MyMDO
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-10">
              <a href="#features" className="text-dark-300 hover:text-white transition-all duration-300 text-sm font-medium">
                Features
              </a>
              <a href="#applications" className="text-dark-300 hover:text-white transition-all duration-300 text-sm font-medium">
                Use Cases
              </a>
              <a href="#how-it-works" className="text-dark-300 hover:text-white transition-all duration-300 text-sm font-medium">
                Learn
              </a>
              <Link to="/register" className="text-dark-300 hover:text-white transition-all duration-300 text-sm font-medium">
                Request a Demo
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <Link to="/app" className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:from-primary-400 hover:to-primary-500 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40">
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="px-5 py-2.5 text-dark-200 hover:text-white font-medium transition-all duration-300">
                    Log In
                  </Link>
                  <Link to="/register" className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:from-primary-400 hover:to-primary-500 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40">
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2.5 text-dark-300 hover:text-white rounded-lg hover:bg-white/5 transition-all duration-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-6 border-t border-white/5 animate-fadeIn">
              <div className="flex flex-col space-y-4">
                <a href="#features" className="text-dark-300 hover:text-white px-2 py-2 transition-all">Features</a>
                <a href="#applications" className="text-dark-300 hover:text-white px-2 py-2 transition-all">Use Cases</a>
                <a href="#how-it-works" className="text-dark-300 hover:text-white px-2 py-2 transition-all">Learn</a>
                <Link to="/register" className="text-dark-300 hover:text-white px-2 py-2 transition-all">Request a Demo</Link>
                <hr className="border-white/10 my-2" />
                {isAuthenticated ? (
                  <Link to="/app" className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg text-center">Go to Dashboard</Link>
                ) : (
                  <>
                    <Link to="/login" className="px-6 py-3 text-dark-200 hover:text-white font-medium text-center">Log In</Link>
                    <Link to="/register" className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg text-center">Sign Up</Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Enhanced */}
      <section className="pt-36 pb-24 px-4 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
                <span className="bg-gradient-to-r from-green-400 via-yellow-400 to-blue-500 bg-clip-text text-transparent">Cloud Platform for Multidisciplinary Design Optimization</span>
              </h1>
              <p className="text-xl text-dark-300 mb-4 leading-relaxed">
                Solve complex design problems faster, smarter, and in the cloud.
              </p>
              <p className="text-lg text-dark-400 mb-12 leading-relaxed">
                MyMDO helps engineers and researchers optimize designs across aerodynamics, structures, and more, with advanced optimization tools and cloud computing.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <Link to="/register" className="group px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl hover:from-primary-400 hover:to-primary-500 transition-all duration-300 shadow-xl shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5">
                  Request Access
                  <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">&rarr;</span>
                </Link>
                <a href="#demo" className="px-8 py-4 bg-white/5 text-white font-semibold rounded-xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 backdrop-blur-sm">
                  Sample Simulation
                </a>
              </div>
            </div>

            {/* Wing Optimization GIF */}
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/30 via-blue-500/20 to-green-500/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-60 group-hover:opacity-80"></div>
              <div className="relative aspect-video bg-dark-900 rounded-2xl border border-cyan-500/20 overflow-hidden shadow-2xl shadow-cyan-500/10">
                <img
                  src="/images/wing-optimization.gif"
                  alt="Wing Aerostructural Optimization - CFD Simulation"
                  className="w-full h-full object-contain"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/60 via-transparent to-transparent pointer-events-none"></div>
                {/* Label */}
                <div className="absolute bottom-4 left-4 px-4 py-2 bg-dark-900/80 rounded-lg text-sm text-cyan-400 backdrop-blur-sm border border-cyan-500/20 flex items-center gap-2">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                  Aerostructural Optimization
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why MyMDO Section - Enhanced */}
      <section className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why MyMDO?
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyMyMDO.map((item, index) => (
              <div
                key={index}
                className="group relative p-8 bg-dark-800/50 rounded-2xl border border-white/5 hover:border-primary-500/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary-500/10 backdrop-blur-sm text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500/20 to-primary-600/20 text-primary-400 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-500">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-dark-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced */}
      <section id="features" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Features
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative p-8 bg-dark-800/50 rounded-2xl border border-white/5 hover:border-white/10 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl backdrop-blur-sm"
              >
                <div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 ${
                    feature.color === 'primary'
                      ? 'bg-gradient-to-br from-primary-500/20 to-primary-600/20 text-primary-400'
                      : feature.color === 'accent'
                      ? 'bg-gradient-to-br from-accent-500/20 to-accent-600/20 text-accent-400'
                      : 'bg-gradient-to-br from-success-500/20 to-success-600/20 text-success-400'
                  }`}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-dark-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works - Enhanced */}
      <section id="how-it-works" className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="text-7xl font-bold bg-gradient-to-b from-dark-700 to-transparent bg-clip-text text-transparent mb-4 group-hover:from-primary-500/30 transition-all duration-500">{step.number}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-dark-400 leading-relaxed">{step.description}</p>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[calc(100%+1rem)] w-[calc(100%-2rem)] h-px bg-gradient-to-r from-dark-700 via-dark-600 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Potential Applications - Enhanced */}
      <section id="applications" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Potential Applications
            </h2>
            <p className="text-dark-400 max-w-2xl mx-auto mb-4">
              Primary focus on aerospace design optimization, with extensions to other engineering fields.
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {applications.map((app, index) => (
              <div
                key={index}
                className={`group relative p-6 rounded-2xl text-center transition-all duration-500 hover:-translate-y-1 backdrop-blur-sm ${
                  app.primary
                    ? 'bg-gradient-to-b from-primary-500/10 to-primary-600/5 border border-primary-500/30 hover:border-primary-500/50 shadow-lg shadow-primary-500/10'
                    : 'bg-dark-800/50 border border-white/5 hover:border-white/10 hover:shadow-xl'
                }`}
              >
                {app.primary && (
                  <span className="inline-block px-4 py-1.5 text-xs font-semibold bg-primary-500/20 text-primary-400 rounded-full mb-4">
                    Primary Focus
                  </span>
                )}
                <h3 className="text-lg font-semibold text-white mb-2">{app.title}</h3>
                <p className="text-dark-400 text-sm leading-relaxed">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/40 via-dark-900 to-accent-900/40"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-500/10 via-transparent to-transparent"></div>

        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Start Optimizing Your Designs Today
          </h2>
          <p className="text-dark-300 text-lg mb-10 max-w-2xl mx-auto">
            Sign up for early access to MyMDO and run your first simulation in the cloud.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="group px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl hover:from-primary-400 hover:to-primary-500 transition-all duration-300 shadow-xl shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5">
              Request Access
              <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
            <a href="#features" className="px-8 py-4 bg-white/5 text-white font-semibold rounded-xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 backdrop-blur-sm">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/5 bg-dark-950/50">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-dark-500 text-sm">
            © {new Date().getFullYear()} MyMDO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
